import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  HtmlTrans,
  SectionHeader,
  SingleSelectionRadio,
  SingleSelectionRadioProps,
  Typography,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import { MutationStatus } from 'react-query';
import AmendedSubjectExamFees from './AmendedSubjectExamFees';
import FormHeader from '../../../components/form-items/FormHeader';
import Declaration from '../../../components/Declaration';
import {
  useGetReviewExamAmendment,
  usePostPayExamAmendment,
} from '../../../api/candidate/exam/@candidate/queries-services/queries';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import { fallbackEmptyString } from '../../../utils/StringUtils';
import RouteKey from '../../../routes/key';
import ExamDraftMessage from '../../examRegistration/components/ExamDraftMessage';
import {
  ExamPaymentMode,
  ExamPaymentModeKey,
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import GroupExamRegistrationPaymentButton from '../../payment/components/GroupExamRegistrationPaymentButton';
import {
  ExamProceedToPaymentRequestBody,
  ExamProceedToPaymentResponseBody,
  ExamType,
} from '../../../api/candidate/exam/model';
import { candidateService, GenericErrorResponse } from '../../../api';
import { CustomMutateReturn } from '../../../hooks/query';
import { ApplicationType } from '../../../api/candidate/config/model';
import { useGetPaymentAccess } from '../../../api-refactored/financial/useGetPaymentAccess';
import { usePostPayNowProxyDetails } from '../../../api-refactored/exam-registration/usePostPayNowProxyDetails';
import PayNowProxyDetailsModal from './SubjectAmendment/PayNowProxyDetailsModal';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
    },
  },
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  subsection: {
    marginBottom: theme.spacing(3),
  },
}));

const {
  receipt,
  examRegistrations,
  coordinatorExamRegistrations,
  coordinatorViewCandidateReceipt,
} = RouteKey.amendment.child;
const { amendment } = RouteKey.child;

const checkAvailablePaymentMode = (paymentDataOption: ExamPaymentMode): [] | ExamPaymentModeKey[] =>
  (Object.keys(paymentDataOption) as ExamPaymentModeKey[]).filter((key) => paymentDataOption[key]);

const transformPayOption = (
  paymentDataOption: ExamPaymentMode,
  t: TFunction<'translation'>
): SingleSelectionRadioProps['data'] =>
  checkAvailablePaymentMode(paymentDataOption).map((key: ExamPaymentModeKey) => {
    if (key === ExamPaymentModeKey.bankTransferOption) {
      return {
        label: t('exam->>ReviewExam.BankTransfer'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.coordinatorPayOption) {
      return {
        label: t('exam->>ReviewExam.PayForCandidates'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.candidatePayOption) {
      return {
        label: t('exam->>ReviewExam.CandidatesMakePayment'),
        additionalLabel: t('exam->>ReviewExam.CandidatesMakePaymentDescription'),
        value: key,
      };
    }
    return {
      label: '',
      additionalLabel: '',
      value: ExamPaymentModeKey.noPaymentModeSelected,
    };
  });

const handleClickPaymentButton = (
  selectedPaymentMode: ExamPaymentModeKey,
  isGroup: boolean,
  hasNoPaymentOptions: boolean,
  isDeclared: boolean,
  setErrMsg: (errMsg: boolean) => void,
  setPaymentModeNotSelectedErrMsg: (paymentModeNotSelectedErrMsg: boolean) => void,
  callback: () => void
) => {
  const handleNotDeclaredOrProceedFlow = () => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      callback();
    }
    setPaymentModeNotSelectedErrMsg(false);
  };

  if (isGroup && !hasNoPaymentOptions) {
    if (selectedPaymentMode === ExamPaymentModeKey.noPaymentModeSelected) {
      setPaymentModeNotSelectedErrMsg(true);
      setErrMsg(!isDeclared);
    } else {
      handleNotDeclaredOrProceedFlow();
    }
  } else {
    handleNotDeclaredOrProceedFlow();
  }
};

const handlePaymentOption = (
  examAmendment: CustomMutateReturn<
    AxiosResponse<ExamProceedToPaymentResponseBody>,
    AxiosError<AxiosError<GenericErrorResponse>>,
    ExamProceedToPaymentRequestBody,
    unknown
  >,
  applicationUUID: string,
  selectedPaymentMode: ExamPaymentModeKey,
  isGroup: boolean,
  hasNoPaymentOptions: boolean,
  handler: {
    callback: (internalTransactionRef: string) => void;
    onSuccess: () => void;
  },
  method?: string
) => {
  examAmendment.mutate(
    {
      uuid: applicationUUID,
      paymentMode: fallbackEmptyString(method),
    },
    {
      onSuccess: (res) => {
        if (
          isGroup &&
          (selectedPaymentMode === ExamPaymentModeKey.candidatePayOption || hasNoPaymentOptions)
        ) {
          handler.callback(
            `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorViewCandidateReceipt.route}/${applicationUUID}`
          );
        } else if (res.data.paymentSuccess) {
          handler.onSuccess();
        } else {
          handler.callback(res.data.internalTransactionRef || '');
        }
      },
      onError: () => {
        handler.callback('');
      },
    }
  );
};

const handleSuccess = (history: any, isGroup: boolean, applicationUUID: any) => {
  const successUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorViewCandidateReceipt.route}/${applicationUUID}`
    : `/${amendment}/${examRegistrations.route}/${applicationUUID}/${receipt.route}`;
  history.push(successUrl);
};

const getDeclarationContent = (
  isGroup: boolean,
  t: TFunction<'translation'>,
  examTypeCode: string | undefined
) => {
  if (isGroup) {
    if (examTypeCode && examTypeCode === ExamType.GCE) {
      return <HtmlTrans i18nKey="amendment->>ConfirmAmendmentDetails.CoordinatorGceDeclaration" />;
    }
    if (examTypeCode && examTypeCode === ExamType.IPSLE) {
      return (
        <HtmlTrans i18nKey="amendment->>ConfirmAmendmentDetails.CoordinatorIpsleDeclaration" />
      );
    }

    return <HtmlTrans i18nKey="amendment->>ConfirmAmendmentDetails.CoordinatorPsleDeclaration" />;
  }
  return <HtmlTrans i18nKey="exam->>ReviewExam.CandidateConfirmationDeclaration" />;
};

const PROCEED_WITH_AMENDMENT = (t: TFunction<'translation'>) =>
  t('amendment->>ConfirmAmendmentDetails.Proceed with Amendment');

const ConfirmAmendmentDetails = ({ isGroup = false }: { isGroup?: boolean }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const history = useHistory();
  const { uuid: applicationUUID } = useParams<any>();

  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<ExamPaymentModeKey>(
    ExamPaymentModeKey.noPaymentModeSelected
  );
  const [paymentModeNotSelectedErrMsg, setPaymentModeNotSelectedErrMsg] = useState<boolean>(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);
  const [openPayNowProxyModal, setOpenPayNowProxyModal] = useState(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const { data } = useGetReviewExamAmendment(applicationUUID);
  const examAmendment = usePostPayExamAmendment();
  const { data: registrationInfo } = candidateService.exam.candidate.useGetExamRegistration(
    applicationUUID
  );
  const { mutate: postPayNowProxyDetails } = usePostPayNowProxyDetails({
    onSuccess: () => {
      examAmendment.mutate(
        {
          uuid: applicationUUID,
          paymentMode: '',
        },
        {
          onSuccess: () => {
            setApiButtonStatus('success');
            handleSuccess(history, isGroup, applicationUUID);
          },
          onError: () => setApiButtonStatus('error'),
        }
      );
    },
    onError: () => setApiButtonStatus('error'),
  });

  const { data: paymentAccessDetails } = useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const paymentDataOptions = {
    /** Order is important here to reflect in the UI, DO NOT change the order * */
    bankTransferOption:
      data?.payment.bankTransferOption || paymentAccessDetails?.bankTransferMethod,
    coordinatorPayOption:
      paymentAccessDetails?.creditCardMethod || paymentAccessDetails?.payNowMethod,
    candidatePayOption: paymentAccessDetails?.candidatePayMethod,
  } as ExamPaymentMode;

  const hasNoPaymentOptions = checkAvailablePaymentMode(paymentDataOptions).length === 0;

  const handleAgreement = () => {
    setErrMsg(false);
    setIsDeclared(!isDeclared);
  };

  const onZeroPaymentHandler = () => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      if (data?.payNowProxyRequired) {
        setOpenPayNowProxyModal(true);
      } else {
        examAmendment.mutate(
          {
            uuid: applicationUUID,
            paymentMode: '',
          },
          {
            onSuccess: () => {
              handleSuccess(history, isGroup, applicationUUID);
            },
          }
        );
      }
    }
  };

  const onClickPaymentButton = (callback: any) => {
    handleClickPaymentButton(
      selectedPaymentMode,
      isGroup,
      hasNoPaymentOptions,
      isDeclared,
      setErrMsg,
      setPaymentModeNotSelectedErrMsg,
      () => callback(true)
    );
  };

  const onHandlePayment = (callback: any, method: string | undefined) => {
    handlePaymentOption(
      examAmendment,
      applicationUUID,
      selectedPaymentMode,
      isGroup,
      hasNoPaymentOptions,
      {
        callback,
        onSuccess: onPaymentAlreadySuccess,
      },
      method
    );
  };

  const onPaymentAlreadySuccess = () => handleSuccess(history, isGroup, applicationUUID);
  return (
    <>
      {data && (
        <ContentTemplate type="full">
          <SectionHeader
            title={t('amendment->>ConfirmAmendmentDetails.Confirm Amendment Details')}
          />
          <PersonalDetails examCandidateDetail={data.candidateDetails} defaultExpanded />
          <ContactDetails contactDetails={data.candidateDetails} />
          <NextOfKinContactDetails {...data.candidateDetails} />
          <AmendedSubjectExamFees {...data} />

          {isGroup &&
            data.paymentRequired &&
            checkAvailablePaymentMode(paymentDataOptions).length > 0 && (
              <Box id="payment-mode">
                <Divider />
                <Typography variant="h3" className={classes.subsection}>
                  {t('exam->>ReviewExam.Payment Mode')}
                </Typography>
                <SingleSelectionRadio
                  id="single-selection-radio-payment-mode"
                  data={transformPayOption(paymentDataOptions, t)}
                  onChange={(event) =>
                    setSelectedPaymentMode(event.target.value as ExamPaymentModeKey)
                  }
                  value={selectedPaymentMode}
                />
              </Box>
            )}

          <Divider />
          <FormHeader title={t('amendment->>ConfirmAmendmentDetails.Declaration')} />
          <Declaration
            noTitle
            isAgreed={isDeclared}
            handleAgreement={handleAgreement}
            customContent={getDeclarationContent(isGroup, t, registrationInfo?.examType.code)}
          />

          {isGroup && paymentModeNotSelectedErrMsg && (
            <Grid xs={12} md={8}>
              <Typography className={classes.errorText} display="inline" variant="body2">
                {t('components->>Payment.PleaseSelectPaymentMode')}{' '}
              </Typography>
            </Grid>
          )}

          {errMsg && (
            <Grid xs={12} md={8}>
              <Typography className={classes.errorText} display="inline" variant="body2">
                {t('components->>Declaration.Please confirm declaration before you proceed.')}
              </Typography>
            </Grid>
          )}
          <Grid container className={classes.actionContainer}>
            <Grid container xs={12} md={12} spacing={2}>
              <Grid xs={12} md={3}>
                <Button
                  fullWidth
                  className={classes.backButton}
                  variant="outlined"
                  onClick={() => history.goBack()}
                >
                  {t('amendment->>ConfirmAmendmentDetails.Back')}
                </Button>
              </Grid>
              <Grid xs={12} md={4}>
                {data.paymentRequired && !isGroup && (
                  <PaymentButton
                    buttonClassName={classes.paymentButton}
                    isUniqueLink={false}
                    disabled={isPaymentDisabled}
                    onPaymentComplete={(isRetry) => {
                      setIsPaymentDisabled(isRetry);
                    }}
                    paymentMethodsAvailable={paymentAccessDetailsOption}
                    onClickPaymentButton={onClickPaymentButton}
                    onHandlePaymentMethod={onHandlePayment}
                    onSuccessPayment={onPaymentAlreadySuccess}
                    fullWidth
                    buttonText={PROCEED_WITH_AMENDMENT(t)}
                    buttonVariant="contained"
                  />
                )}
                {data.paymentRequired && isGroup && !hasNoPaymentOptions && (
                  <GroupExamRegistrationPaymentButton
                    isUniqueLink={false}
                    disabled={isPaymentDisabled}
                    onPaymentComplete={(isRetry) => {
                      setIsPaymentDisabled(isRetry);
                    }}
                    selfPayMethodsAvailable={paymentAccessDetailsOption}
                    onClickPaymentButton={onClickPaymentButton}
                    onHandlePaymentMethod={onHandlePayment}
                    onSuccessPayment={onPaymentAlreadySuccess}
                    buttonText={PROCEED_WITH_AMENDMENT(t)}
                    buttonVariant="contained"
                    customConfirmationModalContent={t('exam->>ReviewExam.ConfirmationPrompt')}
                    coordinatorExamPaymentMode={selectedPaymentMode}
                    zeroDollarPaymentOption={hasNoPaymentOptions}
                    statusPaymentOffline={
                      examAmendment.status !== 'success' ? examAmendment.status : 'idle'
                    }
                    applicationType={ApplicationType.AMENDMENT}
                    buttonErrorText={t('components->>PaymentExceptionError.AmendmentMessage')}
                  />
                )}
                {!data.paymentRequired && (
                  <ApiButton
                    fullWidth
                    status={examAmendment.status}
                    className={classes.paymentButton}
                    onClick={onZeroPaymentHandler}
                    id="button-proceed-with-amendment"
                  >
                    {PROCEED_WITH_AMENDMENT(t)}
                  </ApiButton>
                )}
                <PayNowProxyDetailsModal
                  modalState={openPayNowProxyModal}
                  setOpenModalState={setOpenPayNowProxyModal}
                  postPayNowProxyDetails={postPayNowProxyDetails}
                  applicationUuid={applicationUUID}
                  apiButtonStatus={apiButtonStatus}
                  setApiButtonStatus={setApiButtonStatus}
                />
              </Grid>
            </Grid>
          </Grid>
          <ExamDraftMessage />
        </ContentTemplate>
      )}
    </>
  );
};

export default ConfirmAmendmentDetails;
